import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface ClientRiderMapping {
  id: string;
  clientName: string | null;
  clientRiderId: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RiderClientMappingsTabProps {
  riderId: string;
}

const RiderClientMappingsTab: React.FC<RiderClientMappingsTabProps> = ({ riderId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [mappings, setMappings] = useState<ClientRiderMapping[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<ClientRiderMapping | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientRiderId, setNewClientRiderId] = useState('');
  const [adding, setAdding] = useState(false);

  // Fetch client rider mappings
  const fetchMappings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/v1/riders/${riderId}/client-riders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch client mappings');
      }

      const data = await response.json();
      if (data.success) {
        setMappings(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch mappings');
      }
    } catch (err: any) {
      console.error('Error fetching client mappings:', err);
      setError(err.message || 'Failed to load client mappings');
      enqueueSnackbar('Failed to load client mappings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (riderId) {
      fetchMappings();
    }
  }, [riderId]);

  const openConfirmDialog = (mapping: ClientRiderMapping) => {
    setSelectedMapping(mapping);
    setConfirmOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmOpen(false);
    setSelectedMapping(null);
  };

  const openAddDialog = () => {
    setAddDialogOpen(true);
    setNewClientName('');
    setNewClientRiderId('');
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setNewClientName('');
    setNewClientRiderId('');
  };

  const handleAddClient = async () => {
    if (!newClientName.trim() || !newClientRiderId.trim()) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' });
      return;
    }

    try {
      setAdding(true);
      const response = await fetch(`/api/v1/riders/${riderId}/client-riders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          clientName: newClientName.trim(),
          clientRiderId: newClientRiderId.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add client');
      }

      const data = await response.json();
      if (data.success) {
        enqueueSnackbar('Client added successfully', { variant: 'success' });
        fetchMappings();
        closeAddDialog();
      } else {
        throw new Error(data.message || 'Failed to add client');
      }
    } catch (err: any) {
      console.error('Error adding client:', err);
      enqueueSnackbar(err.message || 'Failed to add client', { variant: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmPrimary = async () => {
    if (!selectedMapping) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/riders/${riderId}/change-primary-client`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          mappingId: selectedMapping.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to change primary client');
      }

      const data = await response.json();
      if (data.success) {
        enqueueSnackbar('Primary client changed successfully', { variant: 'success' });
        fetchMappings();
        closeConfirmDialog();
      } else {
        throw new Error(data.message || 'Failed to change primary client');
      }
    } catch (err: any) {
      console.error('Error changing primary client:', err);
      enqueueSnackbar(err.message || 'Failed to change primary client', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Client Mappings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          size="small"
          disabled
        >
          Add Client
        </Button>
      </Box>

      {mappings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Client Mappings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This rider has no client mappings configured.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Client Rider ID</TableCell>
                <TableCell>Primary</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="center">Set Primary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {mapping.clientName || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {mapping.clientRiderId || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {mapping.isPrimary ? (
                      <Chip
                        icon={<StarIcon />}
                        label="Primary"
                        color="primary"
                        size="small"
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        icon={<StarBorderIcon />}
                        label="Secondary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(mapping.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(mapping.updatedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {!mapping.isPrimary && (
                      <Tooltip title="Set as Primary">
                        <IconButton
                          size="small"
                          onClick={() => openConfirmDialog(mapping)}
                          color="secondary"
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Client Mapping</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Client Name"
            fullWidth
            variant="outlined"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Client Rider ID"
            fullWidth
            variant="outlined"
            value={newClientRiderId}
            onChange={(e) => setNewClientRiderId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddClient}
            disabled={adding}
          >
            {adding ? 'Adding...' : 'Add Client'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={closeConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Primary Client</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to set <strong>{selectedMapping?.clientName || selectedMapping?.clientRiderId}</strong> as the primary client?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will update the rider's primary client information.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmPrimary}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RiderClientMappingsTab;